/* eslint-disable react-hooks/exhaustive-deps */
import {
  useMemo,
  useState,
  useEffect,
  createContext,
  useCallback,
} from "react";
import {
  AppBar,
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  Toolbar,
  Stack,
  Avatar,
  Tooltip,
  TextField,
  Typography,
  MenuItem,
  Select,
  FormControl,
  CircularProgress,
  Breadcrumbs,
  Chip,
  Divider,
  Button,
  Menu,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";

import SearchIcon from "@mui/icons-material/Search";
import LogoutIcon from "@mui/icons-material/Logout";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import HomeIcon from "@mui/icons-material/Home";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import PersonIcon from "@mui/icons-material/Person";
import WorkIcon from "@mui/icons-material/Work";
import VisibilityIcon from "@mui/icons-material/Visibility";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { Outlet, useNavigate, useLocation, Link } from "react-router-dom";
import { styled } from "@mui/system";
import DropdownNotification from "./notification";
import SidebarNavigation from "./SidebarNavigation";
import ThemeSwitcher from "@/components/ui/theme-switcher";
import { useSession } from "@/hooks/useSession";
import { useUsersSettingService } from "@/services/useUsersSettingService";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useThemeContext } from "@/config/useThemeContext";
import { BASE_NAVIGATION } from "@/config/navigation";
import sos from "@/assets/sos.svg";
import useGetOrganizationDetails from "@/hooks/useGetOrganizationDetails";

import { createAppTheme } from "@/config/theme";

const SearchContext = createContext({
  searchTerm: "",
  setSearchTerm: () => {},
  inputValue: "",
  setInputValue: () => {},
  recentSearches: [],
  addRecentSearch: () => {},
});

const drawerWidthOpen = 280;
const drawerWidthCollapsed = 72;

const StyledAvatar = styled(Avatar)(({ theme }) => ({
  width: 36,
  height: 36,
  border: "2px solid",
  borderColor: alpha(theme.palette.common.white, 0.8),
  boxShadow: theme.shadows[2],
  cursor: "pointer",
  transition: theme.transitions.create(["transform", "box-shadow"], {
    duration: theme.transitions.duration.short,
  }),
  "&:hover": {
    transform: "scale(1.05)",
    boxShadow: theme.shadows[4],
  },
}));

const StyledSearchField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    backgroundColor: alpha(theme.palette.common.white, 0.15),
    borderRadius: 8,
    transition: theme.transitions.create(["background-color", "transform"], {
      duration: theme.transitions.duration.short,
    }),
    "&:hover": {
      backgroundColor: alpha(theme.palette.common.white, 0.25),
    },
    "&.Mui-focused": {
      backgroundColor: alpha(theme.palette.common.white, 0.25),
      transform: "scale(1.02)",
    },
  },
  "& .MuiOutlinedInput-input": {
    color: theme.palette.primary.contrastText,
    "&::placeholder": {
      color: alpha(theme.palette.primary.contrastText, 0.7),
    },
  },
  "& .MuiInputLabel-root": {
    color: alpha(theme.palette.primary.contrastText, 0.8),
    "&.Mui-focused": {
      color: theme.palette.primary.contrastText,
    },
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: alpha(theme.palette.primary.contrastText, 0.3),
  },
  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: alpha(theme.palette.primary.contrastText, 0.5),
  },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: theme.palette.primary.contrastText,
    borderWidth: 2,
  },
}));

const QuickActionButton = styled(Button)(({ theme }) => ({
  minWidth: "auto",
  borderRadius: 8,
  padding: theme.spacing(1, 1.5),
  backgroundColor: alpha(theme.palette.common.white, 0.1),
  color: theme.palette.primary.contrastText,
  border: `1px solid ${alpha(theme.palette.common.white, 0.2)}`,
  "&:hover": {
    backgroundColor: alpha(theme.palette.common.white, 0.2),
    transform: "translateY(-1px)",
    boxShadow: theme.shadows[2],
  },
}));

const getInitials = (firstName, lastName) => {
  if (!firstName || !lastName) return "U";
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
};

// Generate breadcrumbs from current path and navigation config
const generateBreadcrumbs = (pathname, navigation, t) => {
  const pathSegments = pathname.split("/").filter(Boolean);

  // Special case for root path ("/")
  if (pathname === "/") {
    return [
      {
        title: t("layout.home"),
        path: "/",
        icon: <HomeIcon fontSize="small" />,
      },
    ];
  }

  // Special case for user guide page
  if (pathname === "/user-guide") {
    return [
      {
        title: t("layout.home"),
        path: "/",
        icon: <HomeIcon fontSize="small" />,
      },
      {
        title: t("userGuide.mainTitle"),
        path: "/user-guide",
        icon: <HelpOutlineIcon fontSize="small" />,
      },
    ];
  }

  const breadcrumbs = [
    {
      title: t("layout.home"),
      path: "/",
      icon: <HomeIcon fontSize="small" />,
    },
  ];

  let currentPath = "";
  let currentNav = navigation;

  for (const segment of pathSegments) {
    currentPath += `/${segment}`;

    // Find matching navigation item
    const findNavItem = (items, path) => {
      for (const item of items) {
        if (
          item.segment === segment ||
          currentPath.endsWith(`/${item.segment}`)
        ) {
          return item;
        }
        if (item.children) {
          const found = findNavItem(item.children, path);
          if (found) return found;
        }
      }
      return null;
    };

    const navItem = findNavItem(currentNav, currentPath);
    if (navItem && navItem.titleKey) {
      // Don't add if it's the same as home (to avoid duplication)
      if (!(navItem.segment === "home" && currentPath === "/home")) {
        breadcrumbs.push({
          title: t(`sidebarNavigation.${navItem.titleKey}`),
          path: currentPath,
          icon: navItem.icon,
        });
      }
    }
  }

  return breadcrumbs;
};

export default function Layout() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { session, logout } = useSession();
  const { userDetails, isLoading: userLoading } = useUsersSettingService(
    session?.id,
    {
      enabled: !!session?.id,
      staleTime: 5 * 60 * 1000,
    }
  );
  const queryClient = useQueryClient();
  const { toggleColorMode, setThemePalette, palette } = useThemeContext();
  const theme = useTheme();
  const mode = theme.palette.mode;
  const paletteOptions = ["orange", "violet", "green", "darkBlue"];
  const {
    organizationDetails,
    organizationDetailsLoading,
    organizationDetailsError,
  } = useGetOrganizationDetails();

  const renderLogo = () => {
    // Show default logo while loading, error, or no logo URL
    if (
      organizationDetailsLoading ||
      organizationDetailsError ||
      !organizationDetails?.logo_url
    ) {
      return (
        <Box
          component="img"
          src={sos}
          alt={t("layout.appLogoAlt")}
          sx={{
            display: { xs: "none", md: "block" },
            height: 40,
            width: "auto",
            minWidth: 100,
            maxWidth: { md: 220 },
            objectFit: "contain",
            cursor: "pointer",
            borderRadius: 1,
            bgcolor: "white",
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            transition: "opacity 0.2s ease-in-out",
            opacity: organizationDetailsLoading ? 0.7 : 1,
          }}
          onClick={() => navigate("/home")}
        />
      );
    }

    // Show organization logo
    return (
      <Box
        component="img"
        src={organizationDetails.logo_url}
        alt={organizationDetails.name || t("layout.appLogoAlt")}
        sx={{
          display: { xs: "none", md: "block" },
          height: 40,
          width: "auto",
          minWidth: 100,
          maxWidth: { md: 220 },
          objectFit: "contain",
          cursor: "pointer",
          borderRadius: 0.5,
          bgcolor: "white",
          p: 0.5,
          border: `0.5px solid ${alpha(theme.palette.divider, 0.1)}`,
          transition: "all 0.2s ease-in-out",
          "&:hover": {
            transform: "scale(1.02)",
          },
          "& svg": {
            width: "100%",
            height: "100%",
          },
        }}
        onClick={() => navigate("/home")}
        onError={(e) => {
          e.currentTarget.src = sos;
        }}
        style={{
          objectFit: "contain",
          padding: "1px",
        }}
      />
    );
  };

  const [collapsed, setCollapsed] = useState(window.innerWidth < 1200);
  const [searchTerm, setSearchTerm] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [recentSearches, setRecentSearches] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("recentSearches") || "[]");
    } catch {
      return [];
    }
  });
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);

  const toggleSidebar = useCallback(() => {
    setCollapsed((prev) => !prev);
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Toggle sidebar with Ctrl/Cmd + B
      if ((event.ctrlKey || event.metaKey) && event.key === "b") {
        event.preventDefault();
        toggleSidebar();
      }

      // Focus search with Ctrl/Cmd + K
      if ((event.ctrlKey || event.metaKey) && event.key === "k") {
        event.preventDefault();
        const searchInput = document.querySelector(
          'input[placeholder*="Search"], input[placeholder*="search"]'
        );
        if (searchInput) {
          searchInput.focus();
        }
      }

      // Quick navigation shortcuts
      if ((event.ctrlKey || event.metaKey) && event.shiftKey) {
        switch (event.key) {
          case "D":
            event.preventDefault();
            navigate("/dashboard");
            break;
          case "P":
            event.preventDefault();
            navigate("/newptw");
            break;
          case "O":
            event.preventDefault();
            navigate("/newobservation");
            break;
          default:
            break;
        }
      }

      // Escape to close modals/menus
      if (event.key === "Escape") {
        setUserMenuAnchor(null);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [navigate, toggleSidebar]);

  // Auto-collapse sidebar on mobile
  useEffect(() => {
    let timeout;
    const handleResize = () => {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        if (window.innerWidth < 768) {
          setCollapsed(true);
        } else if (window.innerWidth >= 1200) {
          setCollapsed(false);
        }
      }, 100);
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      clearTimeout(timeout);
    };
  }, []);

  // Auto-collapse sidebar when navigating to /newptw
  useEffect(() => {
    if (location.pathname === "/newptw") {
      setCollapsed(true);
    }
  }, [location.pathname]);

  const addRecentSearch = useCallback((search) => {
    if (!search.trim()) return;
    setRecentSearches((prev) => {
      const updated = [search, ...prev.filter((s) => s !== search)].slice(0, 5);
      localStorage.setItem("recentSearches", JSON.stringify(updated));
      return updated;
    });
  }, []);

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  const handleSearchSubmit = (e) => {
    if (e.key === "Enter") {
      const newSearchTerm = inputValue.trim();
      if (newSearchTerm) {
        setSearchTerm(newSearchTerm);
        addRecentSearch(newSearchTerm);
        navigate(`/docs?search=${encodeURIComponent(newSearchTerm)}`, {
          replace: true,
        });
      }
    }
  };

  const avatarProps = useMemo(() => {
    if (userLoading || !userDetails) {
      return { children: "U" };
    }
    const url = userDetails?.avatar?.url;
    const initials = getInitials(
      userDetails?.first_name,
      userDetails?.last_name
    );
    return {
      src: url || undefined,
      alt: userDetails?.username || "User",
      children: !url ? initials : null,
    };
  }, [userDetails, userLoading]);

  const breadcrumbs = useMemo(
    () => generateBreadcrumbs(location.pathname, BASE_NAVIGATION, t),
    [location.pathname, t]
  );

  const drawerWidth = collapsed ? drawerWidthCollapsed : drawerWidthOpen;

  const quickActions = [
    {
      label: t("layout.newPTW"),
      icon: <WorkIcon fontSize="small" />,
      path: "/newptw",
      color: "success",
    },
    {
      label: t("layout.newObservation"),
      icon: <VisibilityIcon fontSize="small" />,
      path: "/newobservation",
      color: "info",
    },
  ];

  const drawer = (
    <Box
      sx={{
        backgroundColor: "background.paper",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderRight: 1,
        borderColor: "divider",
      }}
    >
      {/* Header Section */}
      <Box
        sx={{
          py: 1.5, // Reduced vertical padding from 2 to 1.5 for better alignment
          px: 2, // Keep horizontal padding
          borderBottom: 1,
          borderColor: "divider",
          display: "flex",
          alignItems: "center",
          justifyContent: collapsed ? "center" : "space-between",
          minHeight: 72,
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
        }}
      >
        {!collapsed && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {renderLogo()}
          </Box>
        )}
        <IconButton
          onClick={toggleSidebar}
          size="small"
          sx={{
            bgcolor: alpha(theme.palette.common.white, 0.1),
            color: "white",
            "&:hover": {
              bgcolor: alpha(theme.palette.common.white, 0.2),
            },
          }}
        >
          {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
        </IconButton>
      </Box>

      {/* Navigation Section */}
      <Box sx={{ flex: 1, overflow: "auto" }}>
        <SidebarNavigation
          currentPath={location.pathname}
          collapsed={collapsed}
        />
      </Box>

      {/* Footer Section */}
      {!collapsed && (
        <Box
          sx={{
            p: 2,
            borderTop: 1,
            borderColor: "divider",

            bgcolor: alpha(theme.palette.grey[50], 0.3),
          }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", textAlign: "center", fontWeight: 500 }}
          >
            {t("layout.footerText")}
          </Typography>
          <Typography
            variant="caption"
            color="text.disabled"
            sx={{ display: "block", textAlign: "center", mt: 0.5 }}
          >
            v2.1.0 • Enterprise
          </Typography>
        </Box>
      )}
    </Box>
  );

  const appBarContent = useMemo(
    () => (
      <Stack direction="row" spacing={1.5} alignItems="center">
        {/* Quick Actions */}
        <Box
          sx={{
            display: "flex",
            gap: 1,
            flex: 1,
            justifyContent: { xs: "center", sm: "flex-start" },
          }}
        >
          {quickActions.map((action) => (
            <Tooltip key={action.path} title={action.label}>
              <QuickActionButton
                size="small"
                startIcon={action.icon}
                onClick={() => navigate(action.path)}
                sx={{
                  "& .MuiButton-startIcon": {
                    marginRight: { xs: 0, md: 1 },
                  },
                  minWidth: { xs: 40, md: "auto" },
                  px: { xs: 1, md: 1.5 },
                }}
              >
                <Box sx={{ display: { xs: "none", md: "inline" } }}>
                  {action.label}
                </Box>
              </QuickActionButton>
            </Tooltip>
          ))}
        </Box>

        {/* Tablet and Desktop elements */}
        <Box
          sx={{
            display: { xs: "none", sm: "flex" },
            alignItems: "center",
            gap: 1.5,
          }}
        >
          <Divider
            orientation="vertical"
            flexItem
            sx={{
              borderColor: alpha(theme.palette.common.white, 0.2),
              display: { xs: "none", md: "block" },
            }}
          />

          {/* Search - Full field on desktop, expandable on tablet */}
          <Box sx={{ display: { xs: "none", sm: "block" } }}>
            {/* Expandable search on tablet */}
            <Box sx={{ display: { xs: "none", sm: "block", md: "none" } }}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Tooltip
                  title={isSearchExpanded ? "" : t("layout.searchTooltip")}
                >
                  <IconButton
                    onClick={() => setIsSearchExpanded((prev) => !prev)}
                    sx={{
                      color: "primary.contrastText",
                      bgcolor: alpha(theme.palette.common.white, 0.1),
                      "&:hover": {
                        bgcolor: alpha(theme.palette.common.white, 0.2),
                      },
                      ...(isSearchExpanded && {
                        bgcolor: alpha(theme.palette.common.white, 0.2),
                      }),
                    }}
                    size="small"
                  >
                    <SearchIcon />
                  </IconButton>
                </Tooltip>
                <Box
                  sx={{
                    width: isSearchExpanded ? 200 : 0,
                    overflow: "hidden",
                    transition: theme.transitions.create("width", {
                      duration: theme.transitions.duration.shorter,
                    }),
                    ml: isSearchExpanded ? 1 : 0,
                  }}
                >
                  {isSearchExpanded && (
                    <StyledSearchField
                      size="small"
                      placeholder={t("layout.searchPlaceholder")}
                      value={inputValue}
                      onChange={handleInputChange}
                      onKeyDown={(e) => {
                        handleSearchSubmit(e);
                        if (e.key === "Escape") {
                          setIsSearchExpanded(false);
                        }
                      }}
                      autoFocus
                      sx={{
                        width: "100%",
                      }}
                      slotProps={{
                        input: {
                          sx: { py: 0.5 },
                        },
                      }}
                    />
                  )}
                </Box>
              </Box>
            </Box>

            {/* Full search field on desktop */}
            <Box sx={{ display: { xs: "none", md: "block" } }}>
              <StyledSearchField
                size="small"
                placeholder={t("layout.searchPlaceholder")}
                value={inputValue}
                onChange={handleInputChange}
                onKeyDown={handleSearchSubmit}
                sx={{
                  width: 280,
                }}
                slotProps={{
                  input: {
                    startAdornment: (
                      <SearchIcon
                        sx={{ color: "inherit", mr: 1, fontSize: 20 }}
                      />
                    ),
                  },
                }}
              />
            </Box>
          </Box>

          {/* Theme Color Selector - Icon only on tablet */}
          <Box sx={{ display: { xs: "none", sm: "block" } }}>
            <FormControl sx={{ minWidth: { sm: 40, md: 60 } }} size="small">
              <Select
                value={palette}
                onChange={(e) => setThemePalette(e.target.value)}
                renderValue={(value) => {
                  const color = createAppTheme(value, mode).palette.primary
                    .main;
                  return (
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        bgcolor: color,
                        borderRadius: 1,
                        border: "2px solid white",
                        boxShadow: 1,
                      }}
                    />
                  );
                }}
                sx={{
                  color: "primary.contrastText",
                  height: 40,
                  "& .MuiSelect-select": {
                    display: "flex",
                    alignItems: "center",
                    padding: "8px",
                    minWidth: { sm: "unset", md: "60px" },
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: alpha(theme.palette.common.white, 0.3),
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: alpha(theme.palette.common.white, 0.5),
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: theme.palette.common.white,
                  },
                  "& .MuiSvgIcon-root": { color: "primary.contrastText" },
                }}
                slotProps={{
                  menu: {
                    PaperProps: {
                      sx: { p: 1, borderRadius: 2 },
                    },
                  },
                }}
              >
                {paletteOptions.map((name) => {
                  const color = createAppTheme(name, mode).palette.primary.main;
                  const isSelected = name === palette;

                  return (
                    <MenuItem
                      key={name}
                      value={name}
                      sx={{
                        justifyContent: "center",
                        p: 1,
                        borderRadius: 1,
                      }}
                    >
                      <Box
                        sx={{
                          width: 24,
                          height: 24,
                          bgcolor: color,
                          borderRadius: 1,
                          border: "2px solid",
                          borderColor: isSelected ? color : "divider",
                          boxShadow: isSelected ? 2 : 1,
                        }}
                      />
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Box>

          {/* Notification and Theme Switcher - Show on tablet and desktop */}
          <Box sx={{ display: { xs: "none", sm: "flex" }, gap: 1 }}>
            <DropdownNotification />
            <ThemeSwitcher toggleColorMode={toggleColorMode} />
          </Box>
        </Box>

        {userLoading ? (
          <CircularProgress size={36} sx={{ color: "primary.contrastText" }} />
        ) : (
          <Tooltip title={t("layout.userMenu")}>
            <StyledAvatar
              {...avatarProps}
              onClick={(e) => setUserMenuAnchor(e.currentTarget)}
            />
          </Tooltip>
        )}

        <Menu
          anchorEl={userMenuAnchor}
          open={Boolean(userMenuAnchor)}
          onClose={() => setUserMenuAnchor(null)}
          slotProps={{
            paper: {
              sx: {
                mt: 1,
                minWidth: 200,
                borderRadius: 2,
                boxShadow: theme.shadows[8],
              },
            },
          }}
        >
          <MenuItem
            onClick={() => {
              setUserMenuAnchor(null);
              navigate("/profile");
            }}
          >
            <ListItemIcon>
              <PersonIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t("layout.profile")}</ListItemText>
          </MenuItem>
          <Divider />
          <MenuItem
            onClick={() => {
              setUserMenuAnchor(null);
              logout();
              localStorage.removeItem("logoUrl");
              queryClient.clear();
              navigate("/sign-in", { replace: true });
            }}
          >
            <ListItemIcon>
              <LogoutIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t("layout.logout")}</ListItemText>
          </MenuItem>
        </Menu>
      </Stack>
    ),
    [
      palette,
      t,
      toggleSidebar,
      setThemePalette,
      toggleColorMode,
      avatarProps,
      userLoading,
      inputValue,
      userMenuAnchor,
      quickActions,
      navigate,
      logout,
      queryClient,
      organizationDetails, // Add organizationDetails to dependencies
    ]
  );

  return (
    <SearchContext.Provider
      value={{
        searchTerm,
        setSearchTerm,
        inputValue,
        setInputValue,
        recentSearches,
        addRecentSearch,
      }}
    >
      <Box
        sx={{
          display: "flex",
          bgcolor: "background.default",
          minHeight: "100vh",
        }}
      >
        <CssBaseline />
        <AppBar
          position="fixed"
          elevation={0}
          sx={{
            zIndex: (theme) => theme.zIndex.drawer + 1,
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            backdropFilter: "blur(10px)",
            borderBottom: `1px solid ${alpha(theme.palette.common.white, 0.1)}`,
          }}
        >
          <Toolbar
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              minHeight: 72,
              height: 72,
              px: 3,
            }}
          >
            {/* Left side - Logo and menu button */}
            <Box
              sx={{
                display: { xs: "none", md: "flex" },
                alignItems: "center",
                gap: 2,
                flex: "0 0 auto",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                {renderLogo()}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <IconButton
                    onClick={toggleSidebar}
                    size="small"
                    sx={{
                      color: "primary.contrastText",
                      bgcolor: alpha(theme.palette.common.white, 0.1),
                      "&:hover": {
                        bgcolor: alpha(theme.palette.common.white, 0.2),
                        transform: "scale(1.05)",
                      },
                    }}
                  >
                    {collapsed ? (
                      <ChevronRightIcon fontSize="small" />
                    ) : (
                      <ChevronLeftIcon fontSize="small" />
                    )}
                  </IconButton>
                  <Tooltip title={t("sidebarNavigation.userGuideTooltip")}>
                    <IconButton
                      onClick={() => navigate("/user-guide")}
                      size="small"
                      sx={{
                        color: "primary.contrastText",
                        bgcolor: alpha(theme.palette.common.white, 0.1),
                        "&:hover": {
                          bgcolor: alpha(theme.palette.common.white, 0.2),
                          transform: "scale(1.05)",
                        },
                      }}
                    >
                      <HelpOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </Box>

            {/* Mobile Menu Button */}
            <Box
              sx={{
                display: { xs: "flex", md: "none" },
                alignItems: "center",
                gap: 1,
              }}
            >
              <IconButton
                onClick={toggleSidebar}
                size="small"
                sx={{
                  color: "primary.contrastText",
                  bgcolor: alpha(theme.palette.common.white, 0.1),
                  "&:hover": {
                    bgcolor: alpha(theme.palette.common.white, 0.2),
                  },
                }}
              >
                {collapsed ? (
                  <ChevronRightIcon fontSize="small" />
                ) : (
                  <ChevronLeftIcon fontSize="small" />
                )}
              </IconButton>
              <Tooltip title={t("sidebarNavigation.userGuideTooltip")}>
                <IconButton
                  onClick={() => navigate("/user-guide")}
                  size="small"
                  sx={{
                    color: "primary.contrastText",
                    bgcolor: alpha(theme.palette.common.white, 0.1),
                    "&:hover": {
                      bgcolor: alpha(theme.palette.common.white, 0.2),
                    },
                  }}
                >
                  <HelpOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>

            {/* Right side - Actions and user menu */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                ml: "auto",
                flex: "0 0 auto",
              }}
            >
              {appBarContent}
            </Box>
          </Toolbar>
        </AppBar>

        <Box
          component="nav"
          role="navigation"
          aria-label={t("layout.primaryNavigation")}
          sx={{ width: { xs: drawerWidth }, flexShrink: { xs: 0 } }}
        >
          <Drawer
            variant="permanent"
            sx={{
              "& .MuiDrawer-paper": {
                width: drawerWidth,
                transition: theme.transitions.create("width", {
                  easing: theme.transitions.easing.sharp,
                  duration: theme.transitions.duration.enteringScreen,
                }),
                overflowX: "hidden",
                boxSizing: "border-box",
                bgcolor: "background.paper",
                boxShadow: theme.shadows[1],
              },
            }}
            open
          >
            {drawer}
          </Drawer>
        </Box>

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            bgcolor: "background.default",
            minHeight: "100vh",
            pt: "73.5px", // Add padding-top equal to AppBar height
            transition: theme.transitions.create("margin", {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
          }}
        >
          {/* Enhanced App Bar with Breadcrumbs */}
          <Box
            sx={{
              px: { xs: 3, sm: 3 },
              py: 2,
              bgcolor: "background.paper",
              borderBottom: 1,
              borderColor: "divider",
              boxShadow: theme.shadows[1],
            }}
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              alignItems={{ xs: "flex-start", sm: "center" }}
              justifyContent="space-between"
            >
              <Breadcrumbs
                separator={<NavigateNextIcon fontSize="small" />}
                sx={{
                  "& .MuiBreadcrumbs-ol": {
                    flexWrap: "wrap",
                  },
                }}
              >
                {breadcrumbs.map((crumb, index) => {
                  const isLast = index === breadcrumbs.length - 1;
                  return isLast ? (
                    <Chip
                      key={crumb.path}
                      icon={crumb.icon}
                      label={crumb.title}
                      size="small"
                      color="primary"
                      variant="filled"
                    />
                  ) : (
                    <Chip
                      key={crumb.path}
                      icon={crumb.icon}
                      label={crumb.title}
                      size="small"
                      variant="outlined"
                      clickable
                      component={Link}
                      to={crumb.path}
                      sx={{
                        "&:hover": {
                          backgroundColor: "action.hover",
                        },
                      }}
                    />
                  );
                })}
              </Breadcrumbs>
            </Stack>
          </Box>

          {/* Main Content Area */}
          <Box sx={{ px: { xs: 1, sm: 1 }, py: 1 }}>
            <Outlet />
          </Box>
        </Box>
      </Box>
    </SearchContext.Provider>
  );
}

export { SearchContext };
